import { Button, TileRadioGroup } from '@ai-tutor/ui';
import { ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

import type { LessonDepth } from '@/lib/api-client';
import {
  DEPTH_OPTIONS,
  FLOW_PRESETS,
  type FlowPreset,
} from '@/config/lesson-settings-options';
import { useUpdateUserLessonSettings } from '@/hooks/account/useUserLessonSettings';
import { notify } from '@/lib/notify';

import { StepLayout } from '../StepLayout';

/** The shared option sets carry `sub`; the tiles want `description`. */
const DEPTH_TILES = DEPTH_OPTIONS.map(o => ({
  value: o.value,
  label: o.label,
  description: o.sub,
}));
const FLOW_TILES = FLOW_PRESETS.map(o => ({
  value: o.value,
  label: o.label,
  description: o.sub,
}));

interface LessonPrefsStepProps {
  /** Selections are owned by the wizard so they survive navigating back. */
  depth: LessonDepth;
  flow: FlowPreset;
  onDepthChange: (depth: LessonDepth) => void;
  onFlowChange: (flow: FlowPreset) => void;
  /** Called after depth/flow are persisted; advances the wizard. */
  onContinue: () => void;
  onBack: () => void;
}

/**
 * Step 2 of 4 — the two settings that shape every lesson.
 *
 *   depth → default_lesson_settings.options.depth
 *   flow  → default_lesson_settings.flow  (which artifacts get generated)
 *
 * Both arrive pre-filled: from the saved account settings when there are any,
 * otherwise from the product defaults. So this screen is passable in one click,
 * and it deliberately does not say so — a "looks good, skip this" framing on a
 * question the user might want to answer trains them to stop reading.
 *
 * These reuse the existing lesson-settings endpoint, so whatever is chosen here
 * shows up unchanged in Settings → Learning Style. They persist on Continue,
 * NOT on selection: the wizard advances only once the PUT resolves, so a failed
 * save cannot leave the user on the next step believing it was stored.
 * Onboarding is not marked complete here either — that is the final step.
 */
export function LessonPrefsStep({
  depth,
  flow,
  onDepthChange,
  onFlowChange,
  onContinue,
  onBack,
}: LessonPrefsStepProps) {
  const saveLessonSettings = useUpdateUserLessonSettings();
  const isPending = saveLessonSettings.isPending;

  const handleContinue = async () => {
    try {
      await saveLessonSettings.mutateAsync({
        default_lesson_settings: { flow, options: { depth } },
      });
      onContinue();
    } catch (err) {
      notify.error(err, 'Couldn’t save your preferences');
    }
  };

  return (
    <StepLayout
      title='How should your lessons look?'
      description='This applies to every lesson you create, and you can change it anytime in Settings.'
      back={
        <Button
          type='button'
          variant='ghost'
          onClick={onBack}
          disabled={isPending}
        >
          <ArrowLeft className='mr-1 h-4 w-4' />
          Back
        </Button>
      }
      actions={
        <Button type='button' onClick={handleContinue} disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />
              Saving…
            </>
          ) : (
            <>
              Continue
              <ArrowRight className='ml-1.5 h-4 w-4' />
            </>
          )}
        </Button>
      }
    >
      <section>
        <TileRadioGroup
          label='How deep should each lesson go?'
          options={DEPTH_TILES}
          value={depth}
          onChange={onDepthChange}
          columns={3}
        />
      </section>

      <section>
        <TileRadioGroup
          label='What should each lesson include?'
          options={FLOW_TILES}
          value={flow}
          onChange={onFlowChange}
          columns={3}
        />
      </section>
    </StepLayout>
  );
}
